const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidCollectionEmail(email) {
  return EMAIL_REGEX.test(String(email || "").trim());
}

export function normalizeCollectionEmailContacts(contacts = []) {
  const seen = new Set();
  const result = [];

  for (const raw of contacts) {
    if (typeof raw === "string") {
      const email = String(raw || "").trim();
      if (!email || !isValidCollectionEmail(email)) continue;
      const key = email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      result.push({ email, contactName: "" });
      continue;
    }

    const email = String(raw?.email || "").trim();
    if (!email || !isValidCollectionEmail(email)) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const contactName = String(raw?.contactName || "").trim();
    result.push({ email, contactName });
  }

  return result;
}

export function collectionEmailsFromContacts(contacts = []) {
  return normalizeCollectionEmailContacts(contacts).map((c) => c.email);
}

export function normalizeCollectionEmailContactsFromAgenda(agenda) {
  const structured = normalizeCollectionEmailContacts(agenda?.collectionEmailContacts || []);
  if (structured.length > 0) return structured;
  return normalizeCollectionEmailContacts(agenda?.collectionEmails || []);
}

export function splitCollectionEmailContacts(contacts = []) {
  const normalized = normalizeCollectionEmailContacts(contacts);
  return {
    primaryCollectionEmail: normalized[0]?.email || "",
    primaryCollectionContactName: normalized[0]?.contactName || "",
    secondaryCollectionContacts: normalized.slice(1),
  };
}

export function buildCollectionEmailContactsPayload(
  primaryEmail,
  primaryContactName,
  secondaryContacts = [],
) {
  return normalizeCollectionEmailContacts([
    { email: primaryEmail, contactName: primaryContactName },
    ...secondaryContacts,
  ]);
}

export function findCollectionContactNameForEmail(agenda, email) {
  const normalized = String(email || "").trim().toLowerCase();
  if (!normalized) return "";

  const contacts = normalizeCollectionEmailContactsFromAgenda(agenda);
  const match = contacts.find((c) => c.email.toLowerCase() === normalized);
  if (match?.contactName?.trim()) return match.contactName.trim();

  const first = String(agenda?.contactInfo?.firstname || "").trim();
  const last = String(agenda?.contactInfo?.lastname || "").trim();
  return [first, last].filter(Boolean).join(" ").trim();
}

export function resolveRecipientContactNameFromSelection(agenda, selectedEmails = []) {
  const main = selectedEmails?.[0];
  if (main?.contactName?.trim()) return main.contactName.trim();
  if (main?.email) return findCollectionContactNameForEmail(agenda, main.email);
  return "";
}

function formatEmailDisplayLabel(email, contactName) {
  const mail = String(email || "").trim();
  if (!mail) return "";
  const name = String(contactName || "").trim();
  return name ? `${name} · ${mail}` : mail;
}

/**
 * Correos a mostrar en detalle de cobranza: cobranza configurados o, si no hay, generales del contacto.
 */
export function getAgendaCobranzaEmailDisplayLines(agenda) {
  const collection = normalizeCollectionEmailContactsFromAgenda(agenda);
  if (collection.length > 0) {
    return collection.map((c, index) => ({
      email: c.email,
      contactName: c.contactName || "",
      label: formatEmailDisplayLabel(c.email, c.contactName),
      isPrimary: index === 0,
    }));
  }

  const lines = [];
  const generalEmail = String(agenda?.email || "").trim();
  if (generalEmail && isValidCollectionEmail(generalEmail)) {
    const contactName = [agenda?.contactInfo?.firstname, agenda?.contactInfo?.lastname]
      .filter(Boolean)
      .join(" ")
      .trim();
    lines.push({
      email: generalEmail,
      contactName,
      label: formatEmailDisplayLabel(generalEmail, contactName),
      isPrimary: true,
    });
  }

  const secondary = Array.isArray(agenda?.secondaryEmails) ? agenda.secondaryEmails : [];
  secondary.forEach((raw) => {
    const email = String(raw || "").trim();
    if (!email || !isValidCollectionEmail(email)) return;
    if (lines.some((l) => l.email.toLowerCase() === email.toLowerCase())) return;
    lines.push({
      email,
      contactName: "",
      label: email,
      isPrimary: false,
    });
  });

  return lines;
}
